import { DirectionalHint, IconButton, TooltipHost } from '@fluentui/react'
import { NewChatIcon, AboutIcon, HistoryIcon, QuickPromptsIcon } from '../../assets'
import styles from './SidebarMenu.module.css'
import clsx from 'clsx'
import { useSidebarContext } from '../../state/SidebarProvider'
import { useChatContext } from '../../state/ChatProvider'
import { useLocation, useNavigate } from 'react-router-dom'
import { useContext } from 'react'
import { useMediaQuery } from 'react-responsive'
import { AppStateContext } from '../../state/AppProvider'

const calloutProps = {
  styles: {
    root: { borderRadius: '4px' },
    beak: { borderRadius: '2px', left: '-6px !important' },
    beakCurtain: { borderRadius: '4px' },
    calloutMain: { borderRadius: '4px' }
  }
}

const SidebarMenu = () => {
  const appStateContext = useContext(AppStateContext)
  const showChatHistory = !!appStateContext?.state?.frontendSettings?.ui?.show_chat_history_button
  const { isExpanded, toggleSidebar, closeSidebar } = useSidebarContext()
  const { startNewChat, isCitationPanelOpen, setActiveCitation, openQuickPrompts } = useChatContext()
  const navigate = useNavigate()
  const location = useLocation()
  const isMobile = useMediaQuery({ maxWidth: 480 })

  const handleMobileMenuClose = () => {
    if (isMobile) {
      closeSidebar()
    }
  }

  const handleNewChatClick = () => {
    handleMobileMenuClose()
    startNewChat()
  }

  const handleQuickPromptsClick = () => {
    handleMobileMenuClose()
    openQuickPrompts()
  }

  const handleHistoryClick = () => {
    handleMobileMenuClose()

    if (location.pathname !== '/') {
      // Redirect to root and load last history item if available
      // when opening history panel from about page
      navigate('/')
      const chatHistory = appStateContext?.state.chatHistory
      if (chatHistory?.length) {
        appStateContext?.dispatch({ type: 'UPDATE_CURRENT_CHAT', payload: chatHistory[0] })
      }
    }
    appStateContext?.dispatch({ type: 'TOGGLE_CHAT_HISTORY' })
  }

  const handleAboutClick = () => {
    handleMobileMenuClose()
    if (appStateContext?.state.isChatHistoryOpen) {
      appStateContext?.dispatch({ type: 'TOGGLE_CHAT_HISTORY' })
    }
    if (isCitationPanelOpen) {
      setActiveCitation(undefined)
    }
    navigate('/about')
  }

  return (
    <div className={clsx(styles.sidebarMenu, isExpanded ? styles.expanded : styles.collapsed)}>
      <div className={styles.menuButtonContainer}>
        <TooltipHost
          content={isExpanded ? 'Close sidebar' : 'Expand sidebar'}
          id="sidebarMenuButton"
          directionalHint={DirectionalHint.leftCenter}
          calloutProps={calloutProps}>
          <IconButton
            className={styles.menuButton}
            iconProps={{ iconName: isExpanded ? 'CloseIcon' : 'MenuIcon' }}
            styles={{ icon: { margin: 0, width: '24px', height: '24px' } }}
            onClick={toggleSidebar}
            aria-describedby='sidebarMenuButton' />
        </TooltipHost>
      </div>
      {isExpanded ? (
        <div className={styles.menuContainer}>
          <TooltipHost content="Start new chat" id="startNewChat" directionalHint={DirectionalHint.leftCenter} calloutProps={calloutProps}>
            <button className={styles.menuItem} onClick={handleNewChatClick} aria-describedby="startNewChat">
              <img src={NewChatIcon} alt="" />
              <span>New chat</span>
            </button>
          </TooltipHost>
          {
            showChatHistory && (
              <TooltipHost content="Open history" id="openHistory" directionalHint={DirectionalHint.leftCenter} calloutProps={calloutProps}>
                <button className={styles.menuItem} onClick={handleHistoryClick} aria-describedby="openHistory">
                  <img src={HistoryIcon} alt="" />
                  <span>History</span>
                </button>
              </TooltipHost>
            )
          }
          <TooltipHost content="Open quick prompts" id="quickPrompts" directionalHint={DirectionalHint.leftCenter}  >
            <button className={styles.menuItem} onClick={handleQuickPromptsClick} aria-describedby="quickPrompts">
              <img src={QuickPromptsIcon} alt="" />
              <span>Quick prompts</span>
            </button>
          </TooltipHost>
          <TooltipHost content="Navigate to about" id="navToAbout" directionalHint={DirectionalHint.leftCenter} calloutProps={calloutProps}>
            <button className={styles.menuItem} onClick={handleAboutClick} aria-describedby="navToAbout">
              <img src={AboutIcon} alt="" />
              <span>About</span>
            </button>
          </TooltipHost>
        </div>
      ) : (
        <div className={styles.collapsedMenuContainer}>
          <TooltipHost content="Start new chat" directionalHint={DirectionalHint.leftCenter} id="startNewChat" calloutProps={calloutProps}>
            <button onClick={handleNewChatClick} className={styles.iconItem} aria-label="Start new chat">
              <img src={NewChatIcon} alt="" />
            </button>
          </TooltipHost>
          {
            showChatHistory && (
              <TooltipHost content="Open history" directionalHint={DirectionalHint.leftCenter} id="openHistory" calloutProps={calloutProps}>
                <button onClick={handleHistoryClick} className={styles.iconItem} aria-label="Open history">
                  <img src={HistoryIcon} alt="" />
                </button>
              </TooltipHost>
            )}
          <TooltipHost content="Open quick prompts" directionalHint={DirectionalHint.leftCenter} id="quickPrompts">
            <button onClick={handleQuickPromptsClick} className={styles.iconItem} aria-describedby="quickPrompts">
              <img src={QuickPromptsIcon} alt="" />
            </button>
          </TooltipHost>
          <TooltipHost content="Navigate to about" directionalHint={DirectionalHint.leftCenter} id="navToAbout" calloutProps={calloutProps}>
            <button onClick={handleAboutClick} className={styles.iconItem} aria-label="Navigate to about">
              <img src={AboutIcon} alt="" />
            </button>
          </TooltipHost>
        </div>
      )}
    </div>
  )
}

export default SidebarMenu
